import { withNamespaces } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    Button,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    UncontrolledDropdown,
} from 'reactstrap';

import {
    formatMailboxListDate,
    getMailboxMessageDetailPath,
    getMessageDate,
    getMessageFrom,
    getMessageId,
    getMessageSnippet,
    getMessageSubject,
    getMessageTo,
    getThreadActionIds,
    getThreadCount,
    isStarredMessage,
    isUnreadMessage,
    messageHasAttachments,
    SHOW_MAILBOX_ROW_CHECKBOXES,
} from '../../../helpers';
import MessageRowIntents from './MessageRowIntents';

const MessageRow = ({
    t,
    message,
    partyMode = 'from',
    folder = 'inbox',
    folderPath,
    selected = false,
    isActing = false,
    intents = [],
    cases = [],
    applying = null,
    onToggleSelect,
    onToggleStar,
    onToggleRead,
    onTrash,
    onRestore,
    onApplyIntent,
    showAdminControls = true,
}) => {
    const id = getMessageId(message);
    const party = partyMode === 'to' ? getMessageTo(message) : getMessageFrom(message);
    const partyFallback = partyMode === 'to'
        ? t('Unknown recipient')
        : t('Unknown sender');
    const subject = getMessageSubject(message);
    const snippet = getMessageSnippet(message);
    const date = formatMailboxListDate(getMessageDate(message));
    const unread = isUnreadMessage(message);
    const starred = isStarredMessage(message);
    const hasAttachments = messageHasAttachments(message);
    const threadCount = getThreadCount(message);
    const actionIds = getThreadActionIds(message);
    const isTrash = folder === 'trash';
    const checkboxId = `mailbox-message-${id}`;
    const detailPath = {
        pathname: getMailboxMessageDetailPath(id),
        state: {
            mailboxFolderPath: folderPath,
        },
    };
    const disabled = isActing || Boolean(applying);

    if (!id) {
        return null;
    }

    return (
        <li className={`mailbox-message-row${unread ? ' is-unread' : ''}`}>
            {SHOW_MAILBOX_ROW_CHECKBOXES && showAdminControls ? (
                <label
                    className="mailbox-message-row__check"
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                >
                    <input
                        id={checkboxId}
                        type="checkbox"
                        checked={selected}
                        disabled={isActing}
                        onChange={(event) => onToggleSelect?.(id, event.target.checked)}
                        aria-label={t('Select message')}
                    />
                </label>
            ) : null}
            <Button
                color="link"
                className={`mailbox-message-row__star mdi ${starred ? 'mdi-star text-warning' : 'mdi-star-outline'}`}
                title={starred ? t('Unstar') : t('Star')}
                disabled={isActing}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onToggleStar(actionIds, !starred);
                }}
                tabIndex={0}
                style={{ padding: 0, minWidth: 'unset' }}
            />
      
      
            <Link
                to={detailPath}
                className="mailbox-message-row__sender"
            >
                {party || partyFallback}
            </Link>
            {cases.length ? (
                <div className="mailbox-message-row__pills">
                    {cases.map((linkedCase) => {
                        const intentItem = intents.find((item) => item.code === linkedCase.intent);

                        if (!intentItem) {
                            return null;
                        }

                        return (
                            <span
                                key={intentItem.code}
                                className={`mailbox-message-row__pill ${intentItem.pillClass}`}
                            >
                                {t(intentItem.pillTitle)}: {linkedCase.report_secondary_case_id}
                            </span>
                        );
                    })}
                </div>
            ) : null}
            <Link
                to={detailPath}
                className="mailbox-message-row__content"
            >
                {hasAttachments && (
                    <i
                        className="mdi mdi-paperclip me-1"
                        title={t('Has attachments')}
                    />
                )}
                <span className="mailbox-message-row__subject">
                    {subject || t('(No subject)')}
                </span>
                {threadCount > 1 ? (
                    <span className="mailbox-message-row__thread-count">
                        {threadCount}
                    </span>
                ) : null}
                {snippet ? (
                    <span className="mailbox-message-row__snippet">
                        {' – '}
                        {snippet}
                    </span>
                ) : null}
            </Link>
            <div className="mailbox-message-row__date">
                {date}
            </div>
            <div
                className="mailbox-message-row__actions"
                onClick={(event) => event.stopPropagation()}
            >
                {showAdminControls ? (
                    <MessageRowIntents
                        messageId={id}
                        message={message}
                        intents={intents}
                        cases={cases}
                        applying={applying}
                        isActing={isActing}
                        onApplyIntent={onApplyIntent}
                    />
                ) : null}
                <UncontrolledDropdown>
                    <DropdownToggle
                        tag="button"
                        type="button"
                        caret={false}
                        className="btn mailbox-message-row__more"
                        disabled={disabled}
                    >
                        <i className="mdi mdi-dots-vertical" />
                    </DropdownToggle>
                    <DropdownMenu className="dropdown-menu-end">
                        <DropdownItem
                            onClick={() => onToggleRead(actionIds, !unread)}
                        >
                            {unread ? t('Mark as read') : t('Mark as unread')}
                        </DropdownItem>
                        <DropdownItem
                            onClick={() => onToggleStar(actionIds, !starred)}
                        >
                            {starred ? t('Mark as unstarred') : t('Mark as starred')}
                        </DropdownItem>
                        {isTrash ? (
                            <DropdownItem
                                onClick={() => onRestore(actionIds)}
                            >
                                {t('Restore')}
                            </DropdownItem>
                        ) : (
                            <DropdownItem
                                onClick={() => onTrash(actionIds)}
                            >
                                {t('Move to Trash')}
                            </DropdownItem>
                        )}
                    </DropdownMenu>
                </UncontrolledDropdown>
            </div>
        </li>
    );
};

export default withNamespaces()(MessageRow);
